import apiClient from '../../../services/apiClient';
import type { 
  WhatsAppConfig, 
  WhatsAppConfigFormData, 
  MetaValidationResponse,
  EmbeddedSignupCompletePayload,
  EmbeddedSignupStartResponse,
  WhatsAppTestMessage,
  WhatsAppConfigStatus
} from '../types/whatsapp.types';

const BASE_URL = '/whatsapp/api/config';

export class WhatsAppConfigService {
  // CRUD Configuration
  static async getConfig(subscriptionId: number): Promise<WhatsAppConfig | null> {
    try {
      const response = await apiClient.get(`${BASE_URL}/${subscriptionId}/`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) return null;
      throw error;
    }
  }

  static async createConfig(data: WhatsAppConfigFormData): Promise<WhatsAppConfig> {
    const response = await apiClient.post(`${BASE_URL}/`, data);
    return response.data;
  }

  static async updateConfig(id: number, data: Partial<WhatsAppConfigFormData>): Promise<WhatsAppConfig> {
    const response = await apiClient.patch(`${BASE_URL}/${id}/`, data);
    return response.data;
  }

  static async deleteConfig(id: number): Promise<void> {
    await apiClient.delete(`${BASE_URL}/${id}/`);
  }

  // Meta Business API Validation
  static async validateMetaTokens(configId: number): Promise<MetaValidationResponse> {
    const response = await apiClient.post(`${BASE_URL}/${configId}/validate-meta/`);
    return response.data;
  }

  static async setupWebhook(configId: number): Promise<{ webhook_url: string; verify_token: string }> {
    const response = await apiClient.post(`${BASE_URL}/${configId}/setup-webhook/`);
    return response.data;
  }

  // Test Messages
  static async sendTestMessage(configId: number, testData: { to_number: string; message: string }): Promise<WhatsAppTestMessage> {
    const response = await apiClient.post('/whatsapp/api/test-message/', {
      config_id: configId,
      ...testData,
    });
    return {
      to_number: testData.to_number,
      message: testData.message,
      timestamp: new Date().toISOString(),
      status: response.data.success ? 'sent' : 'failed',
    };
  }

  static async getTestHistory(configId: number): Promise<WhatsAppTestMessage[]> {
    const response = await apiClient.get(`${BASE_URL}/${configId}/test-history/`);
    return response.data;
  }

  // Status & Activation
  static async activateConfig(configId: number): Promise<WhatsAppConfig> {
    const response = await apiClient.post(`${BASE_URL}/${configId}/activate/`);
    return response.data;
  }

  static async deactivateConfig(configId: number): Promise<WhatsAppConfig> {
    const response = await apiClient.post(`${BASE_URL}/${configId}/deactivate/`);
    return response.data;
  }

  static async getConfigStatus(configId: number): Promise<WhatsAppConfigStatus> {
    const response = await apiClient.get(`${BASE_URL}/${configId}/status/`);
    return response.data;
  }

  static async startEmbeddedSignup(configId: number): Promise<EmbeddedSignupStartResponse> {
    const response = await apiClient.post(`${BASE_URL}/${configId}/embedded-signup/start/`);
    return response.data;
  }

  static async completeEmbeddedSignup(
    configId: number,
    data: EmbeddedSignupCompletePayload
  ): Promise<WhatsAppConfig> {
    const response = await apiClient.post(
      `${BASE_URL}/${configId}/embedded-signup/complete/`,
      data
    );
    return response.data.config;
  }
}
